import { useState, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useConversionJourney } from "../hooks/useConversionJourney";
import { conversionErrorProperties } from "../utils/conversionJourney";
import { getProductLink } from "../services/BillingService";
import { logOut } from "../services/AuthService";
import { useUserContext } from "../context/UserContext";
import { useStores } from "../hooks/useStores";
import usePageBackground from "../hooks/usePageBackground";
import useExamplePreview from "../hooks/useExamplePreview";
import { geocodeZipCode } from "../utils/geocoding";
import type { WalmartStore, HomeDepotStore } from "../utils/storeUtils";
import { formatHomeDepotStoreId } from "../utils/storeUtils";
import { track } from "../utils/analytics";
import { rememberEmailDeal } from "../utils/emailDealReturn";
import { reportOnboardingStep } from "../services/LifecycleService";
import { hasAnyActiveRetailerPlan } from "../utils/subscriptionGuards";
import { ONLINE_FEED_PATH, hasSavedZip } from "../utils/dealOnboarding";
import { useTranslation } from "../context/LanguageContext";
import type { PaywallVariant } from "../utils/paywallVariant";
import { getOnboardingStorePreview, type OnboardingPreview } from "../services/OnboardingPreviewService";
import { freeSkipLoss, previewPlanTier, previewStoresToPrepick } from "../utils/onboardingPreview";
import {
  STORE_PICKER_RADIUS_MILES,
  dealsFromPreview,
  detectCoverage,
  distanceMiles,
  nearestStores,
  previewHasLocalDeals,
  type Coords,
} from "../utils/onboardingCoverage";
import {
  BackButton,
  ErrorBox,
  ONB_BG,
  ONB_MUTED,
  ONB_TEXT2,
  OnboardingScreen,
  PrimaryCta,
  StoreChecklist,
  TopBar,
} from "../components/onboarding/OnboardingParts";
import { C, ONB_FONT, YellowCta } from "../components/onboarding/DealsFirstParts";
import ZipStepScreen from "../components/onboarding/ZipStepScreen";
import PlansStepScreen, { type PlanOption } from "../components/onboarding/PlansStepScreen";
import NotCoveredScreen from "../components/onboarding/NotCoveredScreen";
import {
  BASIC_PLAN_INTRO_PRICE,
  BASIC_PLAN_PRICE,
  PLUS_PLAN_INTRO_PRICE,
  PLUS_PLAN_PRICE,
  PRO_PLAN_INTRO_PRICE,
  PRO_PLAN_PRICE,
  formatPlanPrice,
} from "../utils/basicPlanOffer";

const BASIC_PRICE_ID = "price_1SpvXMG2cPZes0aXe9CPsPMK";
const PLUS_PRICE_ID = "price_1TfTddG2cPZes0aXmDKAbVOu";
const PRO_PRICE_ID = "price_1SpvZXG2cPZes0aXeCwoqTIa";

// Deals first: ZIP → plans next to this ZIP's own deals (or "not covered") → stores (Walmart, then Home Depot) → checkout.
type StepType = "zipcode" | "plan" | "walmart_stores" | "home_depot_stores";
type StoreStep = "walmart_stores" | "home_depot_stores";
const STORE_STEP_ORDER: StoreStep[] = ["walmart_stores", "home_depot_stores"];

interface Plan {
  id: string;
  name: string;
  price: number;
  discountedPrice: number;
  priceId: string;
  description: string;
  features: string[];
  highlight?: boolean;
  walmartStoreLimit: number;
  homeDepotStoreLimit: number;
}

interface StoreWithDistance {
  normalizedId: string;
  normalizedAddress: string;
  normalizedGeo?: { lat: number; long: number };
  distance?: number;
}

const plans: Plan[] = [
  {
    id: "basic",
    name: "Basic",
    price: BASIC_PLAN_PRICE,
    discountedPrice: BASIC_PLAN_INTRO_PRICE,
    priceId: BASIC_PRICE_ID,
    description: "1 home store + 4 extra nearby.",
    walmartStoreLimit: 1,
    homeDepotStoreLimit: 1,
    features: ["1 home store at Walmart and Home Depot", "4 extra nearby stores at each", "5 price alerts"],
  },
  {
    id: "plus",
    name: "Plus",
    price: PLUS_PLAN_PRICE,
    discountedPrice: PLUS_PLAN_INTRO_PRICE,
    priceId: PLUS_PRICE_ID,
    description: "Up to 3 home stores + 7 extra nearby.",
    walmartStoreLimit: 3,
    homeDepotStoreLimit: 3,
    features: ["Up to 3 home stores per chain", "7 extra nearby stores at Walmart and Home Depot", "9 chains", "7 price alerts"],
  },
  {
    id: "pro",
    name: "Pro",
    price: PRO_PLAN_PRICE,
    discountedPrice: PRO_PLAN_INTRO_PRICE,
    priceId: PRO_PRICE_ID,
    description: "Up to 5 home stores + 10–15 extra nearby.",
    highlight: true,
    walmartStoreLimit: 5,
    homeDepotStoreLimit: 5,
    features: ["Up to 5 home stores per chain", "15 extra nearby at Walmart, 10 at Home Depot", "Hawk Finds", "10 price alerts"],
  },
];

const PLAN_LIST_KEY: Record<string, string> = {
  basic: "onboarding2.plan_basic_list",
  plus: "onboarding2.plan_plus_list",
  pro: "onboarding2.plan_pro_list",
};

// Stores shown per retailer step (closest first), as in pinto's store sheet.
const STORE_LIST_LIMIT = 40;

const formatStoreAddress = (...parts: Array<string | undefined>) =>
  parts.map((part) => part?.trim()).filter(Boolean).join(", ") || "Address unavailable";

const normalizeWalmartStore = (store: WalmartStore): StoreWithDistance => ({
  normalizedId: store.store_id,
  normalizedAddress: formatStoreAddress(store.address, store.city, store.state),
  normalizedGeo: store.geo,
});

const normalizeHomeDepotStore = (store: HomeDepotStore): StoreWithDistance => {
  const lat = parseFloat(store.latitude), lon = parseFloat(store.longitude);
  return {
    normalizedId: formatHomeDepotStoreId(store.store_number),
    normalizedAddress: formatStoreAddress(store.address_1, store.city, store.state),
    normalizedGeo: !isNaN(lat) && !isNaN(lon) ? { lat, long: lon } : undefined,
  };
};

const formatPrice = formatPlanPrice;

/** The next retailer with stores in range; a retailer with none nearby is skipped. */
function nextStoreStep(current: "zipcode" | StoreStep, availability: Record<StoreStep, number>): StoreStep | "checkout" {
  const start = current === "zipcode" ? 0 : STORE_STEP_ORDER.indexOf(current) + 1;
  for (let i = start; i < STORE_STEP_ORDER.length; i += 1) {
    if (availability[STORE_STEP_ORDER[i]] > 0) return STORE_STEP_ORDER[i];
  }
  return "checkout";
}

export default function PaywallPage() {
  const { user, updateUser } = useUserContext();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const knownZip = user && hasSavedZip(user.zipCode) ? user.zipCode! : "";
  const [step, setStep] = useState<StepType>(() => (knownZip ? "plan" : "zipcode"));
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [freeSheetOpen, setFreeSheetOpen] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasActivePlan = hasAnyActiveRetailerPlan(user);
  // This ZIP's store preview: undefined while loading, null when there is none.
  const [previewState, setPreviewState] = useState<{ zip: string; preview: OnboardingPreview | null } | null>(null);
  // "Choose a plan anyway" from the not-covered state, for this ZIP.
  const [planAnywayZip, setPlanAnywayZip] = useState<string | null>(null);
  const journey = useConversionJourney(step);
  const [processedZip, setProcessedZip] = useState<string | null>(null);
  const [zipCoords, setZipCoords] = useState<Coords | null>(null);
  const isStoreStep = step === "walmart_stores" || step === "home_depot_stores";
  usePageBackground(isStoreStep ? ONB_BG : C.page);

  // The plan step is always the list of plans, as in pinto (it won the single-vs-list test).
  const paywallVariant: PaywallVariant = "list";

  useEffect(() => { track("paywall_viewed"); }, []);
  useEffect(() => { rememberEmailDeal(window.location.search); }, []);
  useEffect(() => {
    reportOnboardingStep(step === "plan" ? "paywall" : step === "zipcode" ? "zipcode" : "stores");
  }, [step]);
  const variantViewLogged = useRef(false);
  useEffect(() => {
    if (step !== "plan" || variantViewLogged.current) return;
    variantViewLogged.current = true;
    track("paywall_variant_viewed", { variant: paywallVariant, mode: "setup" });
  }, [step, paywallVariant]);

  const introOfferDiscountsUsed = user?.introOfferDiscountsUsed ?? 0;
  // A win-back email's 50%-off grant applies at checkout even after the intro
  // offers are used up, so show the discounted price for it too.
  const isDiscountEligible = useMemo(
    () => introOfferDiscountsUsed < 3 || !!user?.next50OffCouponPending,
    [introOfferDiscountsUsed, user?.next50OffCouponPending],
  );

  const hasDiscount = (plan?: Plan | null) =>
    isDiscountEligible && (plan?.id === "pro" || plan?.id === "plus" || plan?.id === "basic");

  const [zipCode, setZipCode] = useState(knownZip);
  const [zipDraft, setZipDraft] = useState(knownZip);
  const [selectedWalmartStores, setSelectedWalmartStores] = useState<string[]>([]);
  const [selectedHomeDepotStores, setSelectedHomeDepotStores] = useState<string[]>([]);
  const [nearbyWalmartStores, setNearbyWalmartStores] = useState<StoreWithDistance[]>([]);
  const [nearbyHomeDepotStores, setNearbyHomeDepotStores] = useState<StoreWithDistance[]>([]);
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);

  const { stores: walmartStores, loading: walmartLoading, error: walmartStoresError } = useStores<WalmartStore[]>("walmart");
  const { stores: homeDepotStores, loading: homeDepotLoading, error: homeDepotStoresError } = useStores<HomeDepotStore[]>("home_depot");

  const storeAvailability: Record<StoreStep, number> = {
    walmart_stores: nearbyWalmartStores.length,
    home_depot_stores: nearbyHomeDepotStores.length,
  };
  const zipReady =
    /^\d{5}$/.test(zipCode) && !isGeocodingLoading && processedZip === zipCode && !walmartLoading && !homeDepotLoading;

  const examplePreview = useExamplePreview(true);
  const storePreview = previewState?.zip === zipCode ? previewState.preview : null;
  const previewForView: OnboardingPreview | null | undefined = previewState?.zip === zipCode ? previewState.preview : undefined;

  useEffect(() => {
    journey.once("offers", "offers_available", { result_count: plans.length });
    for (const plan of plans) journey.once(`offer_${plan.id}_${isDiscountEligible}`, "offer_presented", {
      plan_id: plan.id, price: plan.price, intro_price: isDiscountEligible ? plan.discountedPrice : plan.price,
      currency: "USD", offer_available: isDiscountEligible,
    });
  }, [journey, isDiscountEligible]);

  useEffect(() => {
    if (step !== "walmart_stores" && step !== "home_depot_stores") return;
    const retailer = step === "walmart_stores" ? "walmart" : "home_depot";
    const storeError = retailer === "walmart" ? walmartStoresError : homeDepotStoresError;
    const busy = retailer === "walmart" ? walmartLoading : homeDepotLoading;
    const count = retailer === "walmart" ? nearbyWalmartStores.length : nearbyHomeDepotStores.length;
    if (busy) return;
    if (storeError) journey.once(`${step}_error`, "stores_load_failed", { retailer, ...conversionErrorProperties(storeError) });
    else if (!isGeocodingLoading && processedZip === zipCode) journey.once(`${step}_${zipCode}_${count}`, count ? "stores_loaded" : "stores_empty", { retailer, result_count: count });
  }, [journey, step, walmartStoresError, homeDepotStoresError, walmartLoading, homeDepotLoading, nearbyWalmartStores.length, nearbyHomeDepotStores.length, isGeocodingLoading, processedZip, zipCode]);

  useEffect(() => {
    if (knownZip) {
      setZipCode(knownZip);
      setZipDraft((draft) => draft || knownZip);
    }
  }, [knownZip]);

  useEffect(() => {
    if (!zipCode || !/^\d{5}$/.test(zipCode)) return;
    let cancelled = false;
    setProcessedZip(null);
    const processStores = async () => {
      setIsGeocodingLoading(true);
      const coords = await geocodeZipCode(zipCode);
      if (cancelled) return;
      if (!coords) journey.once(`geocode_${zipCode}`, "geocode_unavailable");
      setZipCoords(coords);
      const nearby = (normalized: StoreWithDistance[]) => {
        if (!coords) return normalized.slice(0, 150);
        return normalized.filter(s => s.normalizedGeo?.lat && s.normalizedGeo?.long)
          .map(s => ({ ...s, distance: distanceMiles(coords, s.normalizedGeo!) }))
          .filter(s => s.distance! <= STORE_PICKER_RADIUS_MILES).sort((a, b) => (a.distance || 0) - (b.distance || 0)).slice(0, 150);
      };
      if (walmartStores) setNearbyWalmartStores(nearby(walmartStores.map(normalizeWalmartStore)));
      if (homeDepotStores) setNearbyHomeDepotStores(nearby(homeDepotStores.map(normalizeHomeDepotStore)));
      setProcessedZip(zipCode);
      setIsGeocodingLoading(false);
    };
    processStores().catch(() => {
      if (cancelled) return;
      journey.once(`geocode_${zipCode}`, "geocode_unavailable");
      setZipCoords(null);
      setProcessedZip(zipCode);
      setIsGeocodingLoading(false);
    });
    return () => { cancelled = true; };
  }, [journey, zipCode, walmartStores, homeDepotStores]);

  // Real deals for the plan step. Any failure just shows the plans beside examples.
  useEffect(() => {
    if (step !== "plan" || !/^\d{5}$/.test(zipCode) || previewState?.zip === zipCode) return;
    let cancelled = false;
    void getOnboardingStorePreview(zipCode)
      .catch(() => null)
      .then((preview) => {
        if (cancelled) return;
        setPreviewState({ zip: zipCode, preview });
        if (preview && (previewHasLocalDeals(preview) || (preview.mode === "leads" && preview.leads.length > 0))) {
          journey.event("store_preview_shown", { preview_mode: preview.mode, preview_stores: preview.stores.length, preview_deals: preview.dealCount });
        }
      });
    return () => { cancelled = true; };
  }, [journey, step, zipCode, previewState?.zip]);

  // Not covered = no Walmart or HD within the preview radius (see utils/onboardingCoverage).
  const catalogsSettled = !walmartLoading && !homeDepotLoading;
  const closestStores = useMemo(
    () => nearestStores(zipCoords, walmartStores, homeDepotStores, 3),
    [zipCoords, walmartStores, homeDepotStores],
  );
  const coverage = detectCoverage({
    preview: storePreview,
    resolved: catalogsSettled && processedZip === zipCode && !isGeocodingLoading,
    coords: zipCoords,
    catalogsLoaded: !!walmartStores && !!homeDepotStores && !walmartStoresError && !homeDepotStoresError,
    nearest: closestStores,
  });
  const showNotCovered = step === "plan" && coverage === "none" && planAnywayZip !== zipCode;

  const notCoveredLogged = useRef<string | null>(null);
  useEffect(() => {
    if (!showNotCovered || notCoveredLogged.current === zipCode) return;
    notCoveredLogged.current = zipCode;
    const nearestMiles = closestStores[0] ? Math.round(closestStores[0].miles) : undefined;
    track("onboarding_not_covered_viewed", { has_nearest: closestStores.length > 0, nearest_miles: nearestMiles });
    journey.event("stores_empty", { retailer: "all" });
  }, [showNotCovered, zipCode, closestStores, journey]);

  // Until the shopper chooses, pre-pick the plan sized to what the preview
  // showed (one store → Basic, 2–3 → Plus, more → Pro), as the app does. Email
  // links (win-back, restore) pass ?plan=basic|plus|pro to preselect the plan
  // the shopper had before.
  const requestedPlanId = useMemo(() => new URLSearchParams(window.location.search).get("plan"), []);
  const fittedPlanId = previewPlanTier(storePreview);
  const activePlan: Plan =
    selectedPlan ??
    plans.find((plan) => plan.id === requestedPlanId) ??
    plans.find((plan) => plan.id === fittedPlanId) ??
    plans[0];

  const choosePlan = (plan: Plan) => {
    journey.setContext({ plan_id: plan.id, price: plan.price, intro_price: hasDiscount(plan) ? plan.discountedPrice : plan.price, currency: "USD", offer_available: hasDiscount(plan) });
    journey.event("plan_selected");
    setSelectedPlan(plan);
  };

  const continueFromPlan = () => {
    setError(null);
    if (hasActivePlan) {
      journey.event("billing_existing_subscription");
      setError(t("onboarding2.active_plan_error"));
      return;
    }
    if (!selectedPlan) choosePlan(activePlan);
    journey.event("continue_clicked");
    track("paywall_plan_selected", { plan_id: activePlan.id, plan_name: activePlan.name, price: activePlan.price, variant: paywallVariant });
    if (!/^\d{5}$/.test(zipCode)) {
      setStep("zipcode");
      return;
    }
    if (!zipReady) {
      // Stores for the saved ZIP are still loading; the store step shows a spinner.
      setStep("walmart_stores");
      return;
    }
    const next = nextStoreStep("zipcode", storeAvailability);
    if (next === "checkout") {
      journey.event("stores_empty", { retailer: "all" });
      setError(t("onboarding2.zip_no_stores"));
      return;
    }
    // Pre-pick the Walmarts the preview showed, up to the plan's limit.
    if (next === "walmart_stores" && selectedWalmartStores.length === 0) {
      const prepick = previewStoresToPrepick(storePreview, nearbyWalmartStores.map((store) => store.normalizedId), activePlan.walmartStoreLimit);
      if (prepick.length > 0) setSelectedWalmartStores(prepick);
    }
    setStep(next);
  };

  const signOut = () => {
    queryClient.clear();
    logOut();
  };

  // A new account can still skip the plan for the free online tier (affiliate
  // online deals). The way out sits under the plan CTA, after the user has seen
  // their own store's deals, and confirms against what they would leave behind.
  const canChooseFree = !hasActivePlan && !(user?.retailers?.length);
  const freeLoss = freeSkipLoss(storePreview);

  const openFreeSheet = () => {
    journey.event("free_link_clicked");
    track("paywall_free_link_clicked", { deal_count: freeLoss?.count ?? 0 });
    setFreeSheetOpen(true);
  };

  const keepStoreDeals = () => {
    journey.event("free_skip_kept");
    track("paywall_free_skip_kept", { deal_count: freeLoss?.count ?? 0 });
    setFreeSheetOpen(false);
  };

  const chooseFree = () => {
    journey.event("intent_online_selected");
    track("onboarding_deal_mode_selected", { mode: "online", tier: "free", deal_count: freeLoss?.count ?? 0 });
    // setUp tells the app the online lane was chosen, so it opens the free feed
    // instead of this paywall. Web lets free accounts in either way, so don't wait.
    void updateUser({ setUp: true }).catch(() => undefined);
    setFreeSheetOpen(false);
    navigate(ONLINE_FEED_PATH);
  };

  const goBack = () => {
    journey.event("back_clicked");
    setError(null);
    if (step === "zipcode") setStep("plan");
    else if (step === "walmart_stores") setStep("plan");
    else if (step === "home_depot_stores") setStep(storeAvailability.walmart_stores > 0 ? "walmart_stores" : "plan");
  };

  const openZipStep = () => {
    journey.event("back_clicked");
    setError(null);
    setZipDraft(zipCode);
    setStep("zipcode");
  };

  const proceedToCheckout = async () => {
    if (!selectedPlan) return;
    if (hasActivePlan) { journey.event("billing_existing_subscription"); setError(t("onboarding2.active_plan_error")); return; }
    setLoading("checkout");
    setError(null);
    try {
      journey.event("checkout_requested");
      const response = await getProductLink([selectedPlan.priceId]);
      if (response?.link) {
        track("paywall_checkout_started", { plan_id: selectedPlan.id, walmart_stores: selectedWalmartStores.length, hd_stores: selectedHomeDepotStores.length, checkout_session_reused: response.reused === true, variant: paywallVariant });
        journey.event("checkout_redirected");
        window.location.href = response.link;
      } else { journey.event("checkout_link_missing"); throw new Error("No checkout link received"); }
    } catch (err) {
      journey.event("checkout_failed", conversionErrorProperties(err));
      console.error("Failed to get checkout link:", err);
      setError(t("onboarding2.checkout_failed"));
      track("paywall_checkout_error", { plan_id: selectedPlan.id, error: err instanceof Error ? err.message : String(err) });
    } finally {
      setLoading(null);
    }
  };

  const handleNext = async () => {
    journey.event("continue_clicked", { selected_count: step === "walmart_stores" ? selectedWalmartStores.length : step === "home_depot_stores" ? selectedHomeDepotStores.length : undefined });
    setError(null);
    setLoading("saving");
    try {
      if (step === "zipcode") {
        if (!/^\d{5}$/.test(zipDraft)) { journey.event("invalid_zip"); setError(t("onboarding2.zip_invalid")); setLoading(null); return; }
        if (zipDraft !== user?.zipCode) await updateUser({ zipCode: zipDraft });
        track("onboarding_zip_saved");
        journey.event("step_saved");
        if (zipDraft !== zipCode) {
          setSelectedWalmartStores([]);
          setSelectedHomeDepotStores([]);
        }
        setZipCode(zipDraft);
        setStep("plan");
      } else if (step === "walmart_stores") {
        if (selectedWalmartStores.length === 0) { journey.event("store_selection_missing", { retailer: "walmart" }); setError(t("onboarding2.select_store_error")); setLoading(null); return; }
        await updateUser({ storeIds: selectedWalmartStores, lastStoreChange: Date.now() });
        journey.event("step_saved", { retailer: "walmart", selected_count: selectedWalmartStores.length });
        const next = nextStoreStep("walmart_stores", storeAvailability);
        if (next === "checkout") await proceedToCheckout();
        else setStep(next);
      } else if (step === "home_depot_stores") {
        if (selectedHomeDepotStores.length === 0) { journey.event("store_selection_missing", { retailer: "home_depot" }); setError(t("onboarding2.select_store_error")); setLoading(null); return; }
        await updateUser({ homeDepotStoreIds: selectedHomeDepotStores, lastHomeDepotStoreChange: Date.now() });
        journey.event("step_saved", { retailer: "home_depot", selected_count: selectedHomeDepotStores.length });
        await proceedToCheckout();
      }
    } catch (err) {
      journey.event("save_failed", conversionErrorProperties(err));
      console.error("Failed to save:", err);
      setError(t("onboarding2.save_failed"));
    } finally {
      setLoading(null);
    }
  };

  // "Notify me" saves the ZIP on the account (PATCH /user). An API without the
  // coverageRequest fields rejects them; the card then shows a soft error.
  const requestCoverage = async (email: string) => {
    try {
      await updateUser({ coverageRequestZip: zipCode, coverageRequestEmail: email });
      track("onboarding_coverage_notify", { status: "saved" });
    } catch (err) {
      track("onboarding_coverage_notify", { status: "failed" });
      throw err;
    }
  };

  // ZIP (also "Try another ZIP" / "Change").
  if (step === "zipcode") {
    return (
      <ZipStepScreen
        testId="paywall-zip"
        zip={zipDraft}
        onZipChange={(value) => { setError(null); setZipDraft(value); }}
        onSubmit={() => void handleNext()}
        busy={loading === "saving"}
        error={error}
        onSignOut={signOut}
        onBack={hasSavedZip(user?.zipCode) || hasSavedZip(zipCode) ? goBack : undefined}
        deals={examplePreview === undefined ? undefined : dealsFromPreview(examplePreview)}
      />
    );
  }

  if (showNotCovered) {
    const nearestInRange = closestStores.some((store) => store.miles <= STORE_PICKER_RADIUS_MILES);
    return (
      <NotCoveredScreen
        zip={zipCode}
        examples={examplePreview}
        nearest={closestStores}
        canChoosePlan={nearestInRange}
        defaultEmail={user?.userId ?? ""}
        onNotify={requestCoverage}
        onBrowseFree={() => {
          track("onboarding_not_covered_action", { action: "browse_free" });
          chooseFree();
        }}
        onChoosePlan={() => {
          track("onboarding_not_covered_action", { action: "choose_plan" });
          setPlanAnywayZip(zipCode);
        }}
        onTryZip={() => {
          track("onboarding_not_covered_action", { action: "try_zip" });
          openZipStep();
        }}
        onSignOut={signOut}
      />
    );
  }

  // Plans, deals first.
  if (step === "plan") {
    const discounted = hasDiscount(activePlan);
    const offerPct = discounted ? Math.round((1 - activePlan.discountedPrice / activePlan.price) * 100) : null;
    const introOf = (plan: Plan) => `$${formatPrice(plan.discountedPrice)}`;
    const planOptions: PlanOption[] = plans.map((plan) => ({
      id: plan.id,
      name: plan.name,
      price: hasDiscount(plan) ? introOf(plan) : `$${formatPrice(plan.price)}`,
      wasPrice: hasDiscount(plan) ? `$${formatPrice(plan.price)}` : null,
      description: t(PLAN_LIST_KEY[plan.id]),
    }));
    const ctaPrice = discounted ? introOf(activePlan) : `$${formatPrice(activePlan.price)}${t("onboarding2.per_month")}`;
    const fine = (
      <>
        {discounted
          ? t("onboarding2.fine_intro_then", { intro: introOf(activePlan), price: `$${formatPrice(activePlan.price)}` })
          : t("onboarding2.cancel_anytime")}{" "}
        <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold underline">{t("onboarding2.terms")}</a>
        {" · "}
        <a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold underline">{t("onboarding2.privacy")}</a>
      </>
    );

    return (
      <>
        <PlansStepScreen
          zip={zipCode}
          preview={previewForView}
          examples={examplePreview}
          plans={planOptions}
          activePlanId={activePlan.id}
          onSelectPlan={(id) => {
            const plan = plans.find((candidate) => candidate.id === id);
            if (plan) choosePlan(plan);
          }}
          offerPct={offerPct}
          ctaLabel={t("onboarding2.dp_cta", { plan: activePlan.name, price: ctaPrice })}
          onContinue={continueFromPlan}
          busy={!!loading}
          error={error}
          canChooseFree={canChooseFree}
          onFree={openFreeSheet}
          fine={fine}
          onSignOut={signOut}
          onChangeZip={openZipStep}
        />

        {freeSheetOpen ? (
          <div className="fixed inset-0 z-50 flex flex-col justify-end sm:items-center sm:justify-center" role="dialog" aria-modal="true" aria-labelledby="free-skip-title" style={{ fontFamily: ONB_FONT }}>
            <button type="button" aria-label={t("onboarding2.close")} className="absolute inset-0 bg-black/55" onClick={keepStoreDeals} />
            <div
              className="relative mx-auto flex w-full max-w-[480px] flex-col gap-2.5 rounded-t-[28px] px-5 pb-[max(env(safe-area-inset-bottom),12px)] pt-5 sm:rounded-[28px] sm:pb-5"
              style={{ backgroundColor: C.card, color: C.ink }}
            >
              <h2 id="free-skip-title" className="m-0 text-xl font-black leading-[25px]">
                {freeLoss
                  ? t("onboarding2.free_skip_title_count", { count: freeLoss.count, retailer: freeLoss.retailer })
                  : t("onboarding2.free_skip_title")}
              </h2>
              <p className="m-0 text-[15px] font-medium leading-[21px]" style={{ color: C.muted }}>{t("onboarding2.free_skip_body")}</p>
              <div className="mt-1.5">
                <YellowCta onClick={keepStoreDeals}>{t("onboarding2.free_skip_keep")}</YellowCta>
              </div>
              <button type="button" onClick={chooseFree} className="min-h-[44px] self-center px-3 text-[15px] font-bold hover:underline" style={{ color: C.muted }}>
                {t("onboarding2.free_skip_confirm")}
              </button>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  // Stores: one retailer at a time, closest first, tap to check up to the plan's limit.
  const isWalmartStep = step === "walmart_stores";
  const plan = selectedPlan ?? activePlan;
  const storeLimit = isWalmartStep ? plan.walmartStoreLimit : plan.homeDepotStoreLimit;
  const selectedStores = isWalmartStep ? selectedWalmartStores : selectedHomeDepotStores;
  const setSelectedStores = isWalmartStep ? setSelectedWalmartStores : setSelectedHomeDepotStores;
  const nearbyStores = isWalmartStep ? nearbyWalmartStores : nearbyHomeDepotStores;
  const storesLoading = (isWalmartStep ? walmartLoading : homeDepotLoading) || isGeocodingLoading || processedZip !== zipCode;
  const storesError = isWalmartStep ? walmartStoresError : homeDepotStoresError;
  const isLastStoreStep = !isWalmartStep || nextStoreStep("walmart_stores", storeAvailability) === "checkout";
  const n = selectedStores.length;
  const storesCta =
    loading === "checkout"
      ? t("onboarding2.opening_checkout")
      : n === 0
        ? t("onboarding2.stores_pick_one")
        : isLastStoreStep
          ? t("onboarding2.stores_checkout")
          : t(n === 1 ? "onboarding2.stores_continue_one" : "onboarding2.stores_continue_n", { count: n });

  return (
    <OnboardingScreen testId={`paywall-${step}`} className="h-[100svh]">
      <TopBar
        left={<BackButton onClick={goBack} label={t("onboarding2.back")} />}
        right={
          <button type="button" onClick={signOut} className="px-2 text-[15px] font-bold hover:underline" style={{ color: ONB_MUTED }}>
            {t("onboarding2.sign_out")}
          </button>
        }
      />
      <div className="space-y-1.5 px-6 pb-3 pt-3">
        <h1 className="text-[30px] font-extrabold leading-[34px] tracking-[-1px]">
          {t(isWalmartStep ? "onboarding2.stores_question_walmart" : "onboarding2.stores_question_home_depot")}
        </h1>
        <p className="text-[15px] font-semibold leading-[21px]" style={{ color: ONB_TEXT2 }}>
          {t("onboarding2.stores_hint", { count: storeLimit })}
        </p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 px-4">
        <ErrorBox error={error} />
        <StoreChecklist
          retailer={isWalmartStep ? "walmart" : "home_depot"}
          stores={storesLoading ? null : nearbyStores.slice(0, STORE_LIST_LIMIT)}
          selected={selectedStores}
          limit={storeLimit}
          onChange={setSelectedStores}
          zip={zipCode}
          onChangeZip={openZipStep}
          emptyText={storesError ? t("onboarding2.stores_load_error") : t("onboarding2.none")}
        />
      </div>
      <div className="space-y-2 px-6 pb-[max(env(safe-area-inset-bottom),16px)] pt-3">
        <PrimaryCta onClick={() => void handleNext()} disabled={n === 0 || !!loading} busy={loading === "saving"}>
          {storesCta}
        </PrimaryCta>
        {isLastStoreStep ? (
          <p className="text-center text-xs font-semibold" style={{ color: ONB_MUTED }}>
            {t("onboarding2.secure_checkout")} · {t("onboarding2.cancel_anytime")}
          </p>
        ) : null}
      </div>
    </OnboardingScreen>
  );
}
